import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReportButton from './ReportButton';
import { formatDateTime } from '../../utils/datetime';
import { formatWon, priceTooltipText } from '../../utils/number';

interface Proposal {
 proposal_hash: string;
 proposal_type: 1 | 2 | 3;
 status: string;
 sender_mopeer: string;
 recipient_mopeer: string;
 parent_proposal_hash: string | null;
 root_mail_hash: string | null;
 payload: any;
 expires_at: string;
 created_at: string;
}

const ProposalThread: React.FC = () => {
 const navigate = useNavigate();
 const { hash } = useParams<{ hash: string }>();
 const [proposal, setProposal] = useState<Proposal | null>(null);
 const [chain, setChain] = useState<Proposal[]>([]);
 const [me, setMe] = useState<string | null>(null);
 const [error, setError] = useState<string | null>(null);
 const [busy, setBusy] = useState(false);
 const [showCounterModal, setShowCounterModal] = useState(false);
 const [counterPrice, setCounterPrice] = useState('');
 const [counterDailyCap, setCounterDailyCap] = useState('');
 const [counterGuaranteed, setCounterGuaranteed] = useState('');
 const [counterMessage, setCounterMessage] = useState('');

 const load = useCallback(() => {
  if (!hash) return;
  fetch(`/market/proposals/${hash}`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!j.success) { setError(j.error); return; }
    setProposal(j.data.proposal);
    setChain(j.data.chain);
   });
 }, [hash]);

 useEffect(() => { load(); }, [load]);
 useEffect(() => {
  fetch('/auth/me', { credentials: 'include' })
   .then(r => r.json())
   .then(j => setMe(j?.data?.moPeerHash ?? null))
   .catch(() => {});
 }, []);

 if (error) return <div className="bg-red-50 text-red-700 p-3">{error}</div>;
 if (!proposal) return <div className="text-gray-500 py-8 text-center">로딩 중...</div>;

 const latest = chain.length > 0 ? chain[chain.length - 1] : proposal;
 const isRecipient = me && latest.recipient_mopeer === me;
 const isPending = latest.status === 'pending';

 const callAction = async (path: string, body?: any) => {
  if (!confirm(`'${path}' 액션을 실행할까요?`)) return;
  setBusy(true);
  try {
   const r = await fetch(`/market/proposals/${latest.proposal_hash}/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    credentials: 'include', body: body ? JSON.stringify(body) : undefined,
   });
   const j = await r.json();
   if (!r.ok || !j.success) {
    alert(j.error || `${path} 실패`);
   } else {
    alert(`${path} 완료`);
    load();
   }
  } finally { setBusy(false); }
 };

 return (
  <div className="max-w-3xl mx-auto">
   <button onClick={() => navigate(-1)} className="text-blue-600 hover:text-blue-800 mb-3 text-sm">← 뒤로</button>

   <div className="bg-white border border-gray-200 p-3 md:p-6">
    <h1 className="text-lg md:text-xl font-bold mb-2">제안 체인</h1>
    <p className="text-xs text-gray-500 mb-4">type {proposal.proposal_type} · 현재 상태: {latest.status}</p>

    <div className="space-y-3">
     {chain.map((p, i) => (
      <div key={p.proposal_hash} className={`border-l-2 ${p.proposal_hash === latest.proposal_hash ? 'border-blue-500' : 'border-gray-200'} pl-3 py-1`}>
       <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
         #{i + 1} · {p.sender_mopeer === proposal.sender_mopeer ? '제안자' : '수신자'} 발신 · {formatDateTime(p.created_at)}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100">{p.status}</span>
       </div>
       <div className="text-sm mt-1 space-y-0.5">
        <div>기간: {p.payload?.period_start} ~ {p.payload?.period_end}</div>
        <div>
         단가:{' '}
         <span
          className="cursor-help underline decoration-dotted decoration-gray-400"
          title={priceTooltipText(p.payload?.price_per_impression)}
         >
          {formatWon(p.payload?.price_per_impression)}/회
         </span>
        </div>
        {p.payload?.daily_budget_cap != null && (
         <div>하루 최대 책정 금액: {formatWon(p.payload.daily_budget_cap)}</div>
        )}
        {p.payload?.guaranteed_impressions != null && (
         <div>송출 횟수 보장: {Number(p.payload.guaranteed_impressions).toLocaleString()}회</div>
        )}
        {p.payload?.message && <div className="text-gray-600 mt-1 whitespace-pre-wrap">{p.payload.message}</div>}
       </div>
      </div>
     ))}
    </div>

    {isPending && (
     <div className="border-t border-gray-100 pt-4 mt-4 flex flex-wrap gap-2">
      {isRecipient && (
       <>
        <button disabled={busy} onClick={() => callAction('accept')} className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm disabled:opacity-50">수락</button>
        <button disabled={busy} onClick={() => callAction('reject', { reason: '거절합니다' })} className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm disabled:opacity-50">거부</button>
       </>
      )}
      <button disabled={busy} onClick={() => callAction('close')} className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50">마감</button>
      <button
       disabled={busy}
       onClick={() => {
        setCounterPrice(String(latest.payload?.price_per_impression ?? ''));
        setCounterDailyCap(latest.payload?.daily_budget_cap != null ? String(latest.payload.daily_budget_cap) : '');
        setCounterGuaranteed(latest.payload?.guaranteed_impressions != null ? String(latest.payload.guaranteed_impressions) : '');
        setCounterMessage('');
        setShowCounterModal(true);
       }}
       className="px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm disabled:opacity-50"
      >역제안</button>
     </div>
    )}
    <div className="border-t border-gray-100 pt-2 mt-3 flex justify-end">
     <ReportButton proposal_hash={latest.proposal_hash} />
    </div>
   </div>

   {/* 역제안 모달 */}
   {showCounterModal && (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setShowCounterModal(false)}>
     <div className="bg-white max-w-md w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-lg font-semibold mb-3">역제안</h3>
      <div className="space-y-3 text-sm">
       <div>
        <label className="block text-gray-600 mb-1">
         단가 (원/회){' '}
         <span className="text-gray-400 cursor-help" title={priceTooltipText(Number(counterPrice))}>ⓘ</span>
        </label>
        <input
         type="number" step="0.1" min="0"
         value={counterPrice} onChange={e => setCounterPrice(e.target.value)}
         className="w-32 px-2 py-1 border border-gray-300"
        />
       </div>
       <div className="grid grid-cols-2 gap-2">
        <div>
         <label className="block text-gray-600 mb-1">
          하루 최대 책정 금액 (원){' '}
          <span
           className="text-gray-400 cursor-help"
           title={'광고주 안전 장치 — 그 날 송출이 캡을 넘어도 청구는 캡까지만.\n송출은 계속됩니다 (잉여분은 미디어사 무상 노출).'}
          >ⓘ</span>
         </label>
         <input
          type="number" step="100" min="0"
          value={counterDailyCap} onChange={e => setCounterDailyCap(e.target.value)}
          placeholder="(없음)"
          className="w-full px-2 py-1 border border-gray-300"
         />
         <p className="text-[10px] text-gray-500 mt-0.5">캡까지만 청구 (송출은 계속)</p>
        </div>
        <div>
         <label className="block text-gray-600 mb-1">
          송출 횟수 보장{' '}
          <span
           className="text-gray-400 cursor-help"
           title={'기간 종료 시점 보장치 대비:\n· 50% 미만 송출 → 전액 환불\n· 50~100% 구간 → 실제 송출 비율로 비례 청구\n· 100% 이상 → 보장치 × 단가 정액 청구'}
          >ⓘ</span>
         </label>
         <input
          type="number" step="100" min="0"
          value={counterGuaranteed} onChange={e => setCounterGuaranteed(e.target.value)}
          placeholder="(없음)"
          className="w-full px-2 py-1 border border-gray-300"
         />
         <p className="text-[10px] text-gray-500 mt-0.5">50% 미만 = 전액 환불 / 50~100% = 비례</p>
        </div>
       </div>
       <div>
        <label className="block text-gray-600 mb-1">메시지 (선택)</label>
        <textarea
         value={counterMessage} onChange={e => setCounterMessage(e.target.value)}
         rows={3} className="w-full px-2 py-1 border border-gray-300"
        />
       </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
       <button
        type="button"
        onClick={() => setShowCounterModal(false)}
        disabled={busy}
        className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
       >취소</button>
       <button
        type="button"
        disabled={busy || !counterPrice}
        onClick={async () => {
         setShowCounterModal(false);
         await callAction('counter', {
          payload: {
           ...latest.payload,
           price_per_impression: Number(counterPrice),
           daily_budget_cap: counterDailyCap ? Number(counterDailyCap) : null,
           guaranteed_impressions: counterGuaranteed ? Number(counterGuaranteed) : null,
           message: counterMessage || undefined,
          },
         });
        }}
        className="px-4 py-2 text-sm bg-yellow-500 hover:bg-yellow-600 text-white disabled:opacity-50"
       >역제안 보내기</button>
      </div>
     </div>
    </div>
   )}
  </div>
 );
};

export default ProposalThread;
